import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import usePaginationHook from "hooks/usePagination";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import { storeBannersTableCols } from "utils/tableHelper/storeTableHelper";
import useStyles from "styles/store/storeEditor";
import { sortArray } from "utils/miscUtils";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import {
  copyStoreBanner,
  deleteStoreBanner,
  getStoreBanners,
  updateStoreBannerStatus,
} from "services/storeBannerService";
import ViewImageModal from "screens/modals/ViewImageModal";
import useOpenClose from "hooks/useOpenClose";

interface RowActionsProps {
  data: any;
  actionHandlers: {
    handleDisable?: (id: any) => void;
    handleDelete?: (data: any) => void;
    handleCopyStoreBanner?: (id: any) => void;
  };
}

const RowActions = ({
  data,
  actionHandlers: { handleDisable = () => {}, handleDelete = () => {},  handleCopyStoreBanner = () => {} },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(configData.routes.storeBanners.edit?.replace(":id", data?._id));
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.STORE_BANNERS.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        <MenuItem onClick={() => handleDisable(data?._id)}>
          {data?.isActive ? "Disable": "Enable"}
        </MenuItem>
        <MenuItem onClick={() => handleCopyStoreBanner(data?._id)}>Copy</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => handleDelete(data)}>Delete</MenuItem>
        )}
      </Menu>
    </>
  );
};
const StoreBanners = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.STORE_BANNERS.UPDATE_ROLES
  );

  const { isOpen, close, open, extraData, updateExtra } = useOpenClose();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (v?: any) => {
    setLoading(true);
    try {
      const resp = await getStoreBanners({
        pageNo: 1,
        pageSize: pageSize,
        search: v,
      });
      setFilteredList(
        resp?.data?.map((el) => {
          return {
            ...el,
            isActiveText: el?.isActive ? "Yes" : "No",
            rowDisabled: el?.isActive? false : true,
            copiedFrom: el?.copiedFrom || "-",
          };
        })
      );
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const createPlatform = () => navigate(configData.routes.storeBanners.create);

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.title, id: data?._id });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteStoreBanner(constentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

   const onCopy = async (id: any) => {
    try {
      setLoading(true);
      const resp = await copyStoreBanner(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const onDisable = async (storeId) => {
    try {
      const resp = await updateStoreBannerStatus(storeId);
      showSnackbar(resp?.data?.message);
      fetchData();
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const openModel = (data: any) => {
    updateExtra({
      imageUrl: data.imageUrl,
    });
    open();
  };

  useEffect(() => {
    const initialColumns = storeBannersTableCols().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, hasEditPermission]);

  return (
    <ContentShell
      title="Store Banners"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          {sessionHelper.hasRole(PermittedRoles.STORE_BANNERS.CREATE_ROLES) && (
            <CustomButton onClick={createPlatform}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          actions={RowActions}
          pageSize={pageSize}
          totalEntries={filteredList?.length}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          highlightAltRow={true}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            handleDisable: onDisable,
            openModal: openModel,
            handleCopyStoreBanner: onCopy,
          }}
        />
        <ViewImageModal
          label="Store Banner Image"
          imageSource={extraData.imageUrl}
          isOpen={isOpen}
          close={close}
          customStyle={{ width: "60%" }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default StoreBanners;
